import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { authService } from '../services/api';

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
      };
    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const userData = await authService.getCurrentUser();
      
      // El backend devuelve los datos del usuario directamente, no en un objeto 'user'
      dispatch({ type: 'SET_USER', payload: userData });
    } catch (error: any) {
      console.log('AuthContext: Usuario no autenticado:', error.response?.status);
      dispatch({ type: 'SET_USER', payload: null });
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      console.log('AuthContext: Iniciando login...');
      const response = await authService.login(username, password);
      console.log('AuthContext: Respuesta del login:', response);
      
      if (response.success && response.user) {
        console.log('AuthContext: Login exitoso, usuario:', response.user);
        // Directamente establecer el usuario desde la respuesta del login
        dispatch({ type: 'SET_USER', payload: response.user });
        return true;
      } else if (response.success) {
        console.log('AuthContext: Login exitoso pero sin usuario, obteniendo datos...');
        // Si no viene el usuario en la respuesta, obtenerlo por separado
        await checkAuth();
        return true;
      }
      
      console.log('AuthContext: Login falló:', response);
      return false;
    } catch (error: any) {
      console.error('AuthContext: Error en login:', error);
      console.error('AuthContext: Respuesta del error:', error.response?.data);
      return false;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    state,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};